/**
 * Regras de classificação no pregão: desclassificação, ordenação e campo único do edital.
 * Suporta grupos (cabeçalhos) com renumeração e desclassificação por grupo.
 */

export const TIPO_LINHA_CLASSIFICACAO_CABECALHO = "cabecalho_grupo";

/** Número da colocação (ex.: "3º" → 3); vazio ou inválido → +Infinity. */
export function ordemPorClassificacao(val) {
  if (val == null || val === "") return Number.POSITIVE_INFINITY;
  const m = String(val).match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY;
}

export function isDesclassificado(c) {
  if (!c || typeof c !== "object") return false;
  const d = c.desclassificado;
  return d === true || d === 1 || d === "true" || d === "1";
}

export function isCabecalhoGrupoClassificacao(c) {
  if (!c || typeof c !== "object") return false;
  return c.tipo_linha === TIPO_LINHA_CLASSIFICACAO_CABECALHO;
}

/** Bucket global: `grupo_id` null ou omitido nas linhas de classificação. */
export function chaveGrupoClassificacao(c) {
  if (!c || typeof c !== "object") return null;
  const g = c.grupo_id;
  return g != null && g !== "" ? g : null;
}

export function filtrarLinhasClassificacao(lista) {
  if (!Array.isArray(lista)) return [];
  return lista.filter((c) => !isCabecalhoGrupoClassificacao(c));
}

/** Último `grupo_id` de um cabeçalho no array (para novas linhas). */
export function ultimoGrupoIdCabecalhoClassificacao(lista) {
  for (let i = (lista || []).length - 1; i >= 0; i--) {
    const row = lista[i];
    if (isCabecalhoGrupoClassificacao(row) && row.grupo_id) {
      return row.grupo_id;
    }
  }
  return null;
}

export function contarGruposClassificacao(lista) {
  return (lista || []).filter((c) => isCabecalhoGrupoClassificacao(c)).length;
}

/** Data local no formato YYYY-MM-DD. */
export function dataLocalYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function compararLinhasParaExibicao(a, b) {
  const da = isDesclassificado(a);
  const db = isDesclassificado(b);
  if (da !== db) return da ? 1 : -1;
  if (!da) {
    const cmp =
      ordemPorClassificacao(a.classificacao) - ordemPorClassificacao(b.classificacao);
    if (cmp !== 0) return cmp;
  } else {
    const cmp =
      ordemPorClassificacao(a.classificacao_antes_desclassificacao) -
      ordemPorClassificacao(b.classificacao_antes_desclassificacao);
    if (cmp !== 0) return cmp;
  }
  const idA = a.id ?? 0;
  const idB = b.id ?? 0;
  return idA - idB;
}

function ordenarRunParaExibicao(run) {
  return [...run].sort(compararLinhasParaExibicao);
}

/**
 * Preserva ordem do array (cabeçalhos e blocos); em cada sequência contígua de linhas
 * de classificação com o mesmo `grupo_id`, ordena por colocação / desclassificação.
 */
export function ordenarClassificacoesParaExibicao(lista) {
  if (!Array.isArray(lista)) return [];
  const result = [];
  let i = 0;
  while (i < lista.length) {
    const item = lista[i];
    if (isCabecalhoGrupoClassificacao(item)) {
      result.push(item);
      i += 1;
      continue;
    }
    const firstKey = chaveGrupoClassificacao(lista[i]);
    const run = [];
    while (
      i < lista.length &&
      !isCabecalhoGrupoClassificacao(lista[i]) &&
      chaveGrupoClassificacao(lista[i]) === firstKey
    ) {
      run.push(lista[i]);
      i += 1;
    }
    result.push(...ordenarRunParaExibicao(run));
  }
  return result;
}

function renumerarParticao(particao) {
  const ativos = particao.filter((c) => !isDesclassificado(c));
  const ordenados = [...ativos].sort((a, b) => {
    const cmp =
      ordemPorClassificacao(a.classificacao) - ordemPorClassificacao(b.classificacao);
    if (cmp !== 0) return cmp;
    return (a.id ?? 0) - (b.id ?? 0);
  });
  const idToRank = new Map();
  ordenados.forEach((c, idx) => {
    idToRank.set(c.id, `${idx + 1}º`);
  });
  return idToRank;
}

/**
 * Renumera apenas itens ativos para 1º, 2º, … por grupo (`grupo_id`); bucket global separado.
 * Cabeçalhos inalterados. Itens desclassificados ficam com classificacao vazia.
 */
export function renumerarColocacoesAtivas(lista) {
  if (!Array.isArray(lista)) return [];
  const dataRows = filtrarLinhasClassificacao(lista);
  const byKey = new Map();
  for (const c of dataRows) {
    const k = chaveGrupoClassificacao(c);
    const key = k === null ? "__global__" : String(k);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(c);
  }
  const idToRankGlobal = new Map();
  for (const [, particao] of byKey) {
    const idToRank = renumerarParticao(particao);
    for (const [id, rank] of idToRank) {
      idToRankGlobal.set(id, rank);
    }
  }
  return lista.map((c) => {
    if (isCabecalhoGrupoClassificacao(c)) {
      return c;
    }
    if (isDesclassificado(c)) {
      return { ...c, classificacao: "" };
    }
    const nova = idToRankGlobal.get(c.id);
    return nova != null ? { ...c, classificacao: nova } : { ...c };
  });
}

/**
 * Marca desclassificado (snapshot + data + renumeração) ou reverte se já estiver desclassificado.
 */
export function aplicarDesclassificacao(lista, idItem) {
  if (!Array.isArray(lista)) return [];
  const list = lista.map((c) => ({ ...c }));
  const idx = list.findIndex((c) => c.id === idItem);
  if (idx === -1) return list;
  const item = list[idx];
  if (isCabecalhoGrupoClassificacao(item)) {
    return list;
  }
  if (isDesclassificado(item)) {
    list[idx] = {
      ...item,
      desclassificado: false,
      classificacao_antes_desclassificacao: undefined,
      data_desclassificacao: undefined,
    };
    return renumerarColocacoesAtivas(list);
  }
  const raw = item.classificacao != null ? String(item.classificacao).trim() : "";
  const snap = raw || "—";
  list[idx] = {
    ...item,
    desclassificado: true,
    classificacao_antes_desclassificacao: snap,
    data_desclassificacao: dataLocalYmd(),
  };
  return renumerarColocacoesAtivas(list);
}

/** Melhor colocação entre linhas ativas de todos os grupos (campo único `classificacao` do edital). */
export function primeiraClassificacaoAtiva(lista) {
  const linhas = filtrarLinhasClassificacao(lista || []);
  const ativos = linhas.filter((c) => !isDesclassificado(c));
  if (ativos.length === 0) return "";
  let best = ativos[0];
  let bestOrd = ordemPorClassificacao(best.classificacao);
  for (let k = 1; k < ativos.length; k++) {
    const o = ordemPorClassificacao(ativos[k].classificacao);
    if (o < bestOrd) {
      bestOrd = o;
      best = ativos[k];
    }
  }
  const col = best.classificacao;
  return col != null && String(col).trim() !== "" ? String(col).trim() : "";
}

/** Formata YYYY-MM-DD para exibição pt-BR. */
export function formatarDataDesclassificacao(ymd) {
  if (!ymd || typeof ymd !== "string") return "";
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return ymd;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("pt-BR");
}

/**
 * Percorre `classificacoes` em ordem e devolve blocos: cabeçalho + linhas até o próximo cabeçalho,
 * ou sequência contígua com mesmo `grupo_id` antes do primeiro cabeçalho.
 */
export function segmentosClassificacaoParaUi(lista) {
  if (!Array.isArray(lista)) return [];
  const segmentos = [];
  let i = 0;
  while (i < lista.length) {
    const item = lista[i];
    if (isCabecalhoGrupoClassificacao(item)) {
      const cab = item;
      i += 1;
      const linhas = [];
      while (i < lista.length && !isCabecalhoGrupoClassificacao(lista[i])) {
        linhas.push(lista[i]);
        i += 1;
      }
      segmentos.push({ cabecalho: cab, linhas });
      continue;
    }
    const linhas = [];
    const firstKey = chaveGrupoClassificacao(lista[i]);
    while (
      i < lista.length &&
      !isCabecalhoGrupoClassificacao(lista[i]) &&
      chaveGrupoClassificacao(lista[i]) === firstKey
    ) {
      linhas.push(lista[i]);
      i += 1;
    }
    segmentos.push({ cabecalho: null, linhas });
  }
  return segmentos;
}
